import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { PlantDialog } from "@/components/PlantDialog";
import { CompanionWarnings } from "@/components/CompanionWarnings";
import { stageMeta, formatDate, relativeDays } from "@/lib/plant";
import { Sprout, Droplets, Plus, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MiniGarden } from "@/components/MiniGarden";
import api2 from "@/lib/api";

function StatCard({ icon: Icon, label, value, tint, testId }) {
  return (
    <div className="bg-card rounded-2xl border border-border p-5 shadow-sm animate-rise" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
        <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${tint}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <div className="mt-3 font-display text-3xl font-bold">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/dashboard");
    setData(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const water = async (id) => {
    await api2.post(`/plants/${id}/water`);
    load();
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-sm text-muted-foreground">Welcome back,</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            {(user?.name || "Gardener").split(" ")[0]}'s Garden
          </h1>
        </div>
        <Button onClick={() => { if (!user?.is_premium && data && data.total_sprouts >= 3) { navigate("/pricing"); } else { setDialogOpen(true); } }} className="bg-emerald-700 hover:bg-emerald-800 rounded-xl" data-testid="add-plant-button">
          <Plus className="h-4 w-4 mr-1.5" /> Add Sprout
        </Button>
      </div>

      {!data ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-28 rounded-2xl bg-card border border-border animate-pulse" />
          ))}
        </div>
      ) : data.total_sprouts === 0 ? (
        <div className="space-y-6">
          <EmptyState onAdd={() => setDialogOpen(true)} />
          <MiniGarden />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-emerald-700 to-emerald-600 rounded-2xl p-5 sm:p-6 shadow-sm text-white animate-rise" data-testid="todays-tasks">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-lg font-semibold flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" /> Today's Tasks
              </h2>
              <span className="text-xs bg-white/20 rounded-full px-2.5 py-1 font-medium">{data.needs_water_count} to do</span>
            </div>
            {data.needs_water.length === 0 ? (
              <p className="text-white/90 text-sm">All caught up — nothing needs attention today. Your garden is thriving 🌿</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {data.needs_water.slice(0, 8).map((p) => (
                  <button
                    key={p.id}
                    onClick={() => water(p.id)}
                    className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 backdrop-blur rounded-xl px-3 py-2 text-sm font-medium transition active:scale-[0.98]"
                    data-testid={`task-water-${p.id}`}
                  >
                    <Droplets className="h-4 w-4" /> Water {p.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <CompanionWarnings />

          <div className="grid grid-cols-1 sm:max-w-xs gap-5">
            <StatCard icon={Sprout} label="Total Sprouts" value={data.total_sprouts} tint="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300" testId="stat-total" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <MiniGarden />

            <div className="bg-card rounded-2xl border border-border p-5 sm:p-6 shadow-sm" data-testid="watering-reminder-card">
              <h2 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
                <Droplets className="h-5 w-5 text-sky-500" /> Thirsty Sprouts
              </h2>
              {data.needs_water.length === 0 ? (
                <p className="text-sm text-muted-foreground">Everyone's happily hydrated 🌿</p>
              ) : (
                <div className="space-y-2.5">
                  {data.needs_water.slice(0, 5).map((p) => (
                    <div key={p.id} className="flex items-center justify-between gap-2">
                      <button onClick={() => navigate(`/sprouts/${p.id}`)} className="text-sm font-medium truncate hover:text-emerald-700 text-left">
                        {p.name}
                      </button>
                      <button onClick={() => water(p.id)} className="shrink-0 bg-sky-500 hover:bg-sky-600 text-white text-xs font-medium px-2.5 py-1 rounded-lg flex items-center gap-1" data-testid={`quick-water-${p.id}`}>
                        <Droplets className="h-3.5 w-3.5" /> Water
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-card rounded-2xl border border-border p-5 sm:p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-semibold">Recent Activity</h2>
              <button onClick={() => navigate("/sprouts")} className="text-sm text-emerald-700 dark:text-emerald-400 font-medium flex items-center gap-1 hover:underline">
                View all <ArrowRight className="h-4 w-4" />
              </button>
            </div>
            {data.recent_logs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No logs yet. Add a growth entry to a sprout.</p>
            ) : (
              <div className="space-y-1">
                {data.recent_logs.map((log) => {
                  const meta = stageMeta(log.growth_stage);
                  const Icon = meta.icon;
                  return (
                    <div key={log.id} className="flex items-center gap-3 py-2.5 border-b border-border last:border-0">
                      <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${meta.color}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">
                          {log.plant_name} <span className="text-muted-foreground font-normal">· {log.growth_stage}</span>
                          {log.height_cm != null && <span className="text-muted-foreground font-normal"> · {log.height_cm}cm</span>}
                        </p>
                        {log.note && <p className="text-xs text-muted-foreground truncate">{log.note}</p>}
                      </div>
                      <span className="text-xs text-muted-foreground shrink-0">{relativeDays(log.created_at) || formatDate(log.date)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      <PlantDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={load} />
    </Layout>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="bg-card rounded-2xl border border-dashed border-border p-12 text-center animate-rise" data-testid="dashboard-empty">
      <div className="h-16 w-16 rounded-2xl bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center mx-auto mb-4">
        <Sprout className="h-8 w-8 text-emerald-600" />
      </div>
      <h2 className="font-display text-2xl font-bold">Plant your first seed</h2>
      <p className="text-muted-foreground mt-2 max-w-md mx-auto">
        Add a sprout to start tracking its journey from seed to harvest.
      </p>
      <Button onClick={onAdd} className="mt-6 bg-emerald-700 hover:bg-emerald-800 rounded-xl" data-testid="empty-add-button">
        <Plus className="h-4 w-4 mr-1.5" /> Add your first sprout
      </Button>
    </div>
  );
}
